<template>
  <div class="container" v-if="!loading">
    <templated-controller v-if="isTemplatedProduct" @option-changed="handleOptionChanged">
      <slot/>
    </templated-controller>
    <variable-controller v-else  @option-changed="handleVariantChanged">
      <slot/>
    </variable-controller>
  </div>
</template>
<script>
// import axios from "axios";
import { mapGetters } from "vuex";
import store from "../store";
import smoothscroll from "smoothscroll-polyfill";
import TemplatedController from "./TemplatedController.vue";
import VariableController from './VariableController.vue';
export default {
  store,
  components: { TemplatedController, VariableController },
  name: "ProductCustomizer",
  props: {
    productType: {
      type: String,
      default: "Plain",
    },
    primarySort: {
      type: String,
      default: "",
    },
    featuredImage: {
      type: String,
      default: "",
    },
    isTemplated: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      productOptions: [],
      shippingObject: window.mrST || {},
      legOptions: window.mrL || [],
      fabricOptions: window.mrF || [],
      cushionOptions: window.mrC || [],
      printout: window.mrPT || false,
      productTitle: window.mrP || "Product",
      loading: true,
      regularProduct: false,
      COLOR_KEY: "color",
      SIZE_KEYS: ["width_1", "width_2", "width_3", "depth", "bed_size"],
      OPTION_KEYS: ["option_1", "option_2", "option_3"],
      VARIANT_PARAM: 'variant',
    };
  },
  created() {
    this.loadStore();
    this.setProductStoreVars();
  },
  mounted() {
    smoothscroll.polyfill();
    if (this.isTemplatedProduct) {
      const defaultProduct = this.$store.getters["products/queryProducts"]({
        sku: window.mrSKU,
      });
      if (defaultProduct) {
        this.$store.dispatch("products/setDefaultProduct", defaultProduct);
      }
      this.setInitialTemplatedActiveProduct();
      this.maybeSetOrientationChanges()
      window.addEventListener(
        "orientation-changed",
        this.handleOrientationChanged
      );
    } else {
      this.setInitialVariableActiveProduct()
    }
    this.loading = false;

    // this.testRequest()
    //NOTE THIS WILL SUPPORT
  //   fetch("/products/venice-chair?sections=product-data-json")
  // .then(res => res.json()) 
  },
  computed: {
    searchParameterKeys() {
      // NOTE this is pulling all the keys out of the url for later cleanup, need to watch out for product specific logic
      // that adds or deletes keys, need to add them into it here based on the product type
      let urlKeys = [
        ...new URLSearchParams(this.productOptions[0].query).keys(),
      ];
      return urlKeys;
    },
    isTemplatedProduct() {
      return this.isTemplated == "true";
    },
    drivingAttributes() {
      const attributeKeys = ["orientation"];
      const attributes = {};
      attributeKeys.forEach((key) => {
        attributes[key] = this.activeProduct[key];
      });
      return attributes;
    },
    filteredProducts() {
      return this.productOptions.filter((product) => {
        let showProduct = true;
        Object.keys(this.drivingAttributes).forEach((attr) => {
          if (
            !product[attr] ||
            product[attr] !== this.drivingAttributes[attr]
          ) {
            showProduct = false;
          }
        });
        return showProduct;
      });
    },
    active_width_1() {
      return this.$store.getters["sizes/getActiveSize"]("width_1");
    },
    active_width_2() {
      return this.$store.getters["sizes/getActiveSize"]("width_2");
    },
    active_width_3() {
      return this.$store.getters["sizes/getActiveSize"]("width_3");
    },
    active_depth() {
      return this.$store.getters["sizes/getActiveSize"]("depth");
    },
    active_bed_size() {
      return this.$store.getters["sizes/getActiveSize"]("bed_size");
    },
    ...mapGetters({
      defaultProduct: "products/getDefaultProduct",
      activeFabric: "fabrics/getActiveFabric",
      isDecideLater: "fabrics/isDecideLater",
      activeLeg: "legs/getActiveLeg",
      activeCushion: "cushions/getActiveCushion",
      activeOrientation: "orientations/getActiveOrientation",
    }),
  },
  methods: {
    // testRequest() {
    //   const sectionId = "product-data-json"
    //   // const url = `${window.location.pathname}?sections=${sectionId}`
    //   const url = `/products/hollywood-corner-sectional?sections=${sectionId}`
    //   console.log("FETCHING HOLLYWOOD CORNER SECTIONAL")
    //   axios.get(url).then(({data}) => {
    //     const newData = new DOMParser().parseFromString(data[sectionId], 'text/html').querySelector('script').innerHTML
    //     console.log(JSON.parse(newData))
    //     console.log("GOT HOLLYWOOD CORNER SECTIONAL")
    //   })
    // },
    loadStore() {
      const products = this.products || window.mrPR;
      const sizeObject = {};
      this.SIZE_KEYS.forEach((key) => {
        sizeObject[key] = products[0][key]
          ? [...new Set(products.map((product) => product[key]))]
          : [];
      });
      const storeLoader = {
        "products/setProducts": products,
        "products/setProductTitle": this.productTitle || window.mrP,
        "cushions/setCushions": this.cushions || window.mrC,
        "legs/setLegs": this.legs || window.mrL,
        "fabrics/setFabrics": this.fabrics || window.mrF,
        "fabrics/setPrimarySort": this.primarySort || '',
        "orientations/setOrientations": products[0].orientation
          ? [...new Set(products.map((product) => product.orientation))]
          : [],
        "sizes/setSizes": sizeObject,
        "shipping/setTimelines": this.shippingObject || window.mrST,
        "images/activateImage": this.featuredImage,
        "fabrics/setFabricFilters": this.initializeFabricFilters(this.fabrics || window.mrF),
      };
      if (!this.isTemplatedProduct) {
        // NOTE variable products only have 1 product
        const variants = products[0].variants 
        storeLoader["products/activateProduct"] = { activeProduct: products[0] }
        storeLoader["products/setVariants"] =  variants

        const optionObject = {};
        const labelObject = {}
        const emptyObject = {}
        this.OPTION_KEYS.forEach((key) => {
          optionObject[key] = variants[0][key]
            ? [...new Set(variants.map((variant) => variant[key]))]
            : [];
          const labelValue = products[0][`label_${key}`]
          if (labelValue) {
            labelObject[`label_${key}`] = labelValue.toUpperCase()
            emptyObject[`empty_${key}`] = `Choose your ${labelValue}`
          }
        });
        storeLoader["options/setOptions"] = optionObject
        storeLoader["options/setLabels"] = labelObject
        storeLoader["options/setEmpties"] = emptyObject
      }
      Object.keys(storeLoader).forEach((key) => {
        this.$store.dispatch(key, storeLoader[key]);
      });
      this.$store.dispatch("customizer/configureCustomizer", {
        productType: this.productType || window.mrT,
        isPrintout: window.isPrintout || false,
        productOptions: {
          cushions: this.cushions || window.mrC,
          legs: this.legs || window.mrL,
          fabrics: this.fabrics || window.mrF,
          sizes: this.$store.getters["sizes/getSizes"],
          orientations: this.$store.getters["orientations/getOrientations"],
          options: this.$store.getters["options/getOptions"]
        },
      });
    },
    initializeFabricFilters(fabrics) {
      // TODO at a later date refactor this to a better place
      const colorFilters = []
      const bestForFilters = []
      const fabricTypeFilters = []
      
      fabrics.forEach((fabric) => {
        if (fabric.colors) {
          colorFilters.push(fabric.colors)
        }
        if (fabric.additional_details.best_for && fabric.additional_details.best_for.length) {
          fabric.additional_details.best_for.forEach((filter) => bestForFilters.push(filter))
        } else if (fabric.additional_details.best_for) {
          bestForFilters.push(fabric.additional_details.best_for)
        }
        if (fabric.fabric_options && fabric.fabric_options.length) {
          fabric.fabric_options.forEach((filter) => fabricTypeFilters.push(filter))
        } else if (fabric.fabric_options) {
          fabricTypeFilters.push(fabric.fabric_options)
        }
      })
      
      return [
        {
          heading: "Fabric Type",
          type: "RADIO",
          appValue: "fabric_options",
          emptyOption: "Choose fabric",
          activeFilters: "",
          values:[...new Set(fabricTypeFilters)].map((filter) => {
            return {
              value: filter,
              active: false
            }
          })
        },
        {
          heading: "Color Family",
          type: "COLOR",
          appValue: "colors",
          emptyOption: "Choose family",
          activeFilters: "",
          values:[...new Set(colorFilters)].map((filter) => {
            return {
              value: filter,
              active: false
            }
          }),
        }
      ]
    },
    setInitialTemplatedActiveProduct() {
      const searchParams = new URLSearchParams(window.location.search);
      const queryObject = this.getProductQueryObject(true);
      let activeProduct = this.defaultProduct;
      if (Object.keys(queryObject).length) {
        activeProduct =
          this.$store.getters["products/queryProducts"](queryObject);
      }
      this.$store.dispatch("products/activateProduct", { activeProduct });
      this.maybeSetProductValues(
        activeProduct,
        false,
        searchParams.get(this.COLOR_KEY)
      );
      this.maybeSetProductValues(
        this.defaultProduct,
        true,
        searchParams.get(this.COLOR_KEY)
      );
      this.updateSizes();
      this.setHistoryState();
      this.$store.dispatch("shipping/activateTimeline", activeProduct.sku );
      document.dispatchEvent(new CustomEvent('variant-selected', {
        detail: {
          variantId: activeProduct.id
              ? activeProduct.id
              : activeProduct.variantId
        }
      }));
    },
    setInitialVariableActiveProduct() {

      const defaultVariant = this.$store.getters["products/queryVariants"]({ defaultVariant: true });
      this.$store.dispatch("products/defaultVariant", defaultVariant );

      // Setting the defaults
      this.OPTION_KEYS.forEach((key) => {
        this.$store.dispatch("options/setDefaultOption", {
          size: defaultVariant[key],
          key,
        });
      });

      let activeVariant = defaultVariant
      // If the variant is set by the URL, use that one as the active
      const searchParams = new URLSearchParams(window.location.search);
      const variantId = searchParams.get(this.VARIANT_PARAM)
      if (variantId) {
        activeVariant = this.$store.getters["products/queryVariants"]({ variantId: variantId });
      }

      this.$store.dispatch("products/activateVariant",{ activeVariant });
      // Setting the base actives
      this.OPTION_KEYS.forEach((key) => {
        this.$store.dispatch("options/activateOption", {
          option: activeVariant[key],
          key,
        });
      });
      this.$store.dispatch("shipping/activateTimeline", activeVariant.sku );
    },
    maybeSetProductValues(product, isDefault = false, colorTitle) {
      if (product.leg) {
        const legs = this.$store.getters["legs/queryLegs"]({
          title: product.leg.trim(),
          findMultiple: true,
        });
        // console.log(legs,`${product.leg}`)
        const legAction = isDefault ? "legs/setDefaultLeg" : "legs/activateLeg";
        this.$store.dispatch(legAction, legs[0]);
      }
      if (product.cushion) {
        const cushions = this.$store.getters["cushions/queryCushions"]({
          title: product.cushion,
          findMultiple: true,
        });
        const cushionAction = isDefault
          ? "cushions/setDefaultCushion"
          : "cushions/activateCushion";
        this.$store.dispatch(cushionAction, cushions[0]);
      }
      if (product.fabric) {
        // NOTE array is artifact of previous implmentations
        const decideLater = this.$store.getters["fabrics/getDecideLaterOption"];
        let fabric = decideLater;
        if (product.fabric !== decideLater.title) {
          const queryObject = {
            fabric_options: [product.fabric],
            findMultiple: true,
          };
          if (colorTitle) {
            queryObject["sku_code"] = colorTitle;
          }
          const fabrics =
            this.$store.getters["fabrics/queryFabrics"](queryObject);
          fabric = fabrics[0];
        } else {
          this.$store.dispatch("fabrics/setDecideLater", true)
        }
        const fabricAction = isDefault
          ? "fabrics/setDefaultFabric"
          : "fabrics/activateFabric";
        this.$store.dispatch(fabricAction, fabric);
      }
      if (product.orientation) {
        const orientation = this.$store.getters[
          "orientations/getMatchingOrientation"
        ](product.orientation);
        const orientationAction = isDefault
          ? "orientations/setDefaultOrientation"
          : "orientations/activateOrientation";
        this.$store.dispatch(orientationAction, orientation);
      }

      this.SIZE_KEYS.forEach((key) => {
        if (product[key]) {
          const size = this.$store.getters["sizes/getSize"](key, product[key]);
          const sizeAction = isDefault
            ? "sizes/setDefaultSize"
            : "sizes/activateSize";
          this.$store.dispatch(sizeAction, { size, key });
        }
      });
    },
    setHistoryState() {
      const activeProduct = this.$store.getters["products/getActiveProduct"];
      const additionalParams = new URLSearchParams();
      const existingParams = new URLSearchParams(window.location.search);
      for (const key of existingParams.keys()) {
        if (!activeProduct[key] && existingParams[key]) {
          additionalParams.set(key, existingParams[key]);
        }
      }
      if (this.isDecideLater && additionalParams.has(this.COLOR_KEY)) {
        additionalParams.delete(this.COLOR_KEY);
      } else if (this.activeFabric && this.activeFabric.sku_code) {
        additionalParams.set(this.COLOR_KEY, this.activeFabric.sku_code);
      }
      let searchParams = activeProduct.query;
      if (additionalParams.toString()) {
        // If it has the variant remove the variant so that we can have it function normally
        if (additionalParams.has("variant")) additionalParams.delete("variant");
        searchParams += "&" + additionalParams.toString();
      }
      const newPath = window.location.pathname + searchParams;
      history.replaceState(null, "", newPath);
    },
    handleOptionChanged() {
      const queryObject = this.getProductQueryObject();
      // console.log(queryObject)
      this.updateSizes();
      // console.log(queryObject)
      if (this.activeFabric) {
        queryObject["fabric"] = this.$store.getters["fabrics/getActiveType"];
      }
      if (this.activeLeg) {
        queryObject["leg"] = this.activeLeg.title.trim();
      }
      if (this.activeCushion) {
        queryObject["cushion"] = this.activeCushion.title;
      }
      if (this.activeOrientation) {
        queryObject["orientation"] = this.activeOrientation;
      }
      this.SIZE_KEYS.forEach((key) => {
        if (
          this[`active_${key}`] &&
          Object.keys(this[`active_${key}`]).length
        ) {
          queryObject[key] = this[`active_${key}`];
        }
      });
      // console.log(queryObject)
      const matchingProduct =
        this.$store.getters["products/queryProducts"](queryObject);
      
      if (matchingProduct) {
        this.$store.dispatch("products/activateProduct", {
          activeProduct: { ...matchingProduct },
        });
        this.setHistoryState();
        this.$store.dispatch("shipping/activateTimeline", matchingProduct.sku );
        document.dispatchEvent(new CustomEvent('variant-selected', {
          detail: {
            variantId: matchingProduct.id
              ? matchingProduct.id
              : matchingProduct.variantId
          }
        }));
      } else {
        console.table({
          message: "Matching Product Not Found",
          ...queryObject,
        });
      }
    },
    getProductQueryObject(isDefault = false) {
      const searchParams = new URLSearchParams(window.location.search);
      const queryObject = {};
      const productFunction = isDefault
        ? "products/getDefaultProduct"
        : "products/getActiveProduct";
      const storeProduct = this.$store.getters[productFunction];
      const productObject = storeProduct.activeProduct
        ? storeProduct.activeProduct
        : storeProduct;

      for (const key of searchParams.keys()) {
        if (productObject[key]) {
          queryObject[key] = searchParams.get(key);
        }
      }
      return queryObject;
    },
    setProductStoreVars() {
      // TODO this needs to be computed
      const formattedProdType = this.productType?.toUpperCase() || "";

      const width1SofaWidths = ["SOFA WITH CHAISE", "SOFA"];
      if (width1SofaWidths.includes(formattedProdType)) {
        this.$store.dispatch("sizes/setActiveLabel", {
          label: "WIDTH",
          key: "width_1",
        });
      }

      const uShapedLabels = [
        "U-SHAPED SECTIONAL",
        "U-SHAPED SECTIONAL WITH BUMPER"
      ];
      if (uShapedLabels.includes(formattedProdType)) {
        this.$store.dispatch("sizes/setActiveLabel", {
          label: "SIDE",
          key: "width_1",
        });
        this.$store.dispatch("sizes/setActiveLabel", {
          label: "WIDTH",
          key: "width_2",
        });
      }
      const overallWidthLabels = [
        "U-SHAPED DOUBLE CHAISE SECTIONAL",
        "BENCH SEAT U-SHAPED DOUBLE CHAISE SECTIONAL"
      ]
      if (overallWidthLabels.includes(formattedProdType)) {
        // this.$store.dispatch("sizes/setActiveLabel", {
        //   label: "SIDE",
        //   key: "width_1",
        // });
        this.$store.dispatch("sizes/setActiveLabel", {
          label: "WIDTH",
          key: "width_2",
        });
      }
    },
    handleOrientationChanged() {
      const formattedProdType = this.productType?.toUpperCase() || "";
      if (["SECTIONAL", "SECTIONAL WITH BUMPER"].includes(formattedProdType)) {
        const lastActiveSizes = this.$store.getters["sizes/getActiveSizes"]()

        this.updateSizes();
        // Flip the sizes for these products
        const lastWidth1 = lastActiveSizes["width_1"]
        lastActiveSizes["width_1"] = lastActiveSizes["width_2"]
        lastActiveSizes["width_2"] = lastWidth1
        this.SIZE_KEYS.forEach((key) => {
          this.$store.dispatch("sizes/activateSize", {
            size: lastActiveSizes[key],
            key,
          });
        });
      }
    },
    updateSizes() {
      // const formattedProdType = this.productType?.toUpperCase() || "";
      const queryObject = {
        findMultiple: true,
      };

      if (this.activeOrientation) {
        queryObject["orientation"] = this.activeOrientation;
      }

      const matchingProducts =
        this.$store.getters["products/queryProducts"](queryObject);

      const sizeObject = {};

      this.SIZE_KEYS.forEach((key) => {
        const workingProducts =
          key !== "width_1"
            ?  this.productType?.toUpperCase() !== 'BED' ?
              [...matchingProducts].filter((product) => {
                return product.width_1 == this.active_width_1;
              }):
              [...matchingProducts]
            : [...matchingProducts];

        sizeObject[key] = matchingProducts[0][key]
          ? [...new Set(workingProducts.map((product) => product[key]))]
          : [];
      });
      this.$store.dispatch("sizes/setSizes", sizeObject);

      this.SIZE_KEYS.forEach((key) => {
        const activeSize = this.$store.getters["sizes/getActiveSize"](key);
        if (activeSize && Object.keys(activeSize).length) {
          if (!sizeObject[key].includes(activeSize)) {
            this.$store.dispatch("sizes/activateSize", {
              size: sizeObject[key][0],
              key,
            });
          }
        }
      });
    },
    
    maybeSetOrientationChanges() {
      const activeProduct = this.$store.getters["products/getActiveProduct"] 
      if (activeProduct.orientation && activeProduct.orientation.match(/right/i)?.length) {
        if (activeProduct.title.match(/ibiza\+one\+arm\+bumper\+piece/i)?.length || !activeProduct.title.match(/ibiza/i)?.length ) {
          if (!activeProduct.title.match(/O%27Side\+Out\+Sectional\+with\+Bumper/i)?.length && !activeProduct.title.match(/O%27side\+Out\+Party\+Pass\+U-Sectional/i)?.length) {
            window.dispatchEvent(new CustomEvent('orientation-changed', {detail: activeProduct.orientation}));
          }
          
        }        
      }
    },
    handleVariantChanged() {
      const activeOptions = this.$store.getters['options/getActiveOptions']
      const queryObject = {}
      Object.keys(activeOptions).forEach((key) => {
        if (activeOptions[key].length) {
          queryObject[key] = activeOptions[key]
        }
      })
      const matchingVariant = this.$store.getters['products/queryVariants'](queryObject)
      if (matchingVariant) {
        this.$store.dispatch("products/activateVariant", {
          activeVariant: { ...matchingVariant },
        });
        this.setVariantHistoryState();
        this.$store.dispatch("shipping/activateTimeline", matchingVariant.sku );
        document.dispatchEvent(new CustomEvent('variant-selected', {
          detail: {
            variantId: matchingVariant.id
          }
        }));
      } else {
        console.table({
          message: "Matching Variant Not Found",
          ...queryObject,
        });
      }
    },
    setVariantHistoryState() {
      const activeVariant = this.$store.getters["products/getActiveVariant"];
      const windowParams = new URLSearchParams(window.location.search);
      windowParams.set('variant', activeVariant.variantId)
      const newPath = [window.location.pathname,windowParams.toString()].join('?');
      history.replaceState(null, "", newPath);
    },
  },
};
</script>
<style scoped>
.container {
  /* Colors */
  --text-color: #2C2B2A;
  --hover-option-background: #d4d6d9;
  --hover-option-foreground: #000;
  --active-option-background: #000;
  --active-option-foreground: #fff;
  --additional-details-background: #E8EEF0;
  --option-details-background: #FBF6F2;
  --option-details-foreground: #000;
  --border-color: #2C2B2A;
  --button-background: #2C2B2A;
  --button-foreground: #fff;
  --error-background: #fecaca;
  --error-foreground: #991b1b;
  --general-foreground: #000;
  --sale-price-color: #991b1b;
  --modal-overlay-color: #3a3a41;
  --modal-content-background: #fff;
  --modal-content-foreground: #000;
  --modal-option-text: #fff;
  --modal-option-active-ring: #000;
  --modal-border-color: #000;
  --modal-image-background-color: #d4d6d9;
  --detail-background-color: rgba(58, 58, 65, 0.2);
  /* Opacities */
  --hover-opacity: 0.8;
  --disabled-button-opacity: 0.5;
  --modal-overlay-opacity: 0.6;
  /* Misc */
  --border-thickness: 1px;
  --strikethrough-thickness: 2px;
}
.container >>> .visually-hidden {
  clip: rect(0, 0, 0, 0);
  overflow: hidden;
  position: absolute;
  height: 1px;
  width: 1px;
}
.container >>> .interaction-container {
  display: grid;
  padding: 0;
  -moz-column-gap: 7px;
  column-gap: 7px;
  color: var(--general-foreground);
  grid-template-columns: auto 1fr;
  line-height: 1;
  align-items: start;
  width: 100%;
  position: relative;
}
.container >>> .dropdown__container {
  position: relative;
}
.container >>> .dropdown {
  position: absolute;
  /* right: 0; */
  z-index: 1;
  background-color: var(--active-option-foreground);
  top: 0;
  transition: z-index 0s .2s;
}
.container >>> .active .dropdown {
  z-index: 2;
  transition: z-index 0s;
}
.container >>> label,
.container >>> .label {
  align-items: start;
  color: var(--general-foreground, #000);
  font-family: Visby;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 30px;
  text-transform: capitalize;
  color: var(--text-color, #2c2b2a);
  min-width: 85px;
  letter-spacing: 0;
}
.container >>> .interaction-container--half {
  align-items: start;
}
@media screen and (min-width: 1024px) {
  .container >>> .interaction-container {
    grid-template-columns: auto 1fr;
  }
  .container >>> label,
  .container >>> .label {
    font-size: 16px;
  }
}
</style>
